import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './AuthContext';
import { PermissionsProvider } from './PermissionsContext';
import Layout from './Layout';
import ProtectedRoute from './ProtectedRoute';
import ErrorBoundary from './ErrorBoundary';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Public routes
import LandingPage from './LandingPage';
import Login from './Login';
import Register from './Register';
import PasswordRecovery from './PasswordRecovery';
import PasswordReset from './PasswordReset';
import RegistrationSuccess from './RegistrationSuccess';
import EmploymentApplicationForm from './EmploymentApplicationForm';
import Unauthorized from './Unauthorized';
import KnowledgeBaseHome from './kb/KnowledgeBaseHome';
import KBArticle from './kb/KBArticle';
import KBCategoryView from './kb/KBCategoryView';

// Protected routes
import AdminDashboard from './AdminDashboard';
import MainDashboard from './MainDashboard';
import ManagerDashboard from './ManagerDashboard';
import ManagerPermissions from './ManagerPermissions';
import SoloDashboard from './solo/SoloDashboard';
import EmployeePortal from './EmployeePortal';
import WeeklyForecastDashboard from './WeeklyForecastDashboard';
import EmployeeCard from './EmployeeCard';
import ManageApplications from './ManageApplications';
import EmployeeReviews from './EmployeeReviews';
import HolidayCalendar from './HolidayCalendar';
import HolidayApproval from './HolidayApproval';
import SubmitHolidayRequest from './SubmitHolidayRequest';
import SubmitAbsence from './SubmitAbsence';
import SupplierCard from './SupplierCard';
import IncomeCard from './IncomeCard';
import Notes from './Notes';
import PersonalInfo from './PersonalInfo';
import SubmitTraining from './SubmitTraining';
import Disciplinary from './Disciplinary';
import RentalDashboard from './RentalDashboard';
import RentalLayout from './RentalLayout';
import DocumentApproval from './DocumentApproval';
import Reminders from './Reminders';
import MessagesDashboard from './Messages_Dashboard';
import SubmitEmployeeReview from './SubmitEmployeeReview';
import AuditTrail from './AuditTrail';
import TaxReport from './TaxReport';
import VatDashboard from './VatDashboard';
import CashflowReport from './CashflowReport';
import ProfitLossReport from './ProfitLossReport';
import ReportsDashboard from './ReportsDashboard';
import TransactionReport from './TransactionReport';
import DailyIncomeEntry from './DailyIncomeEntry';
import CommissionDashboard from './CommissionDashboard';
import CommissionTypeEditor from './CommissionTypeEditor';
import EmployeeCommission from './EmployeeCommission';
import PayrollCalculator from './PayrollCalculator';
import ProductManagement from './ProductManagement';
import StockTransactions from './StockTransactions';
import ServiceProductManager from './ServiceProductManager';
import PurchaseOrders from './PurchaseOrders';
import ServiceCalculator from './ServiceCalculator';
import ServiceEditor from './ServiceEditor';
import TimeOffRequestForm from './TimeOffRequestForm';
import TimeOffApproval from './TimeOffApproval';
import TimeOffHub from './TimeOffHub';
import MyTimeOffRequests from './MyTimeOffRequests';
import FlexibleWorkingRequestForm from './FlexibleWorkingRequestForm';
import FlexibleWorkingApproval from './FlexibleWorkingApproval';
import MyFlexibleWorkingRequests from './MyFlexibleWorkingRequests';
import BradfordFactorDashboard from './BradfordFactorDashboard';
import RotaDashboard from './RotaDashboard';
import RotaBuilder from './RotaBuilder';
import MySchedule from './MySchedule';
import SSPCalculator from './SSPCalculator';
import LeaveBalances from './LeaveBalances';
import Timesheet from './Timesheet';
import ClockWidget from './ClockWidget';
import PayrollExport from './PayrollExport';
import CompanySettings from './CompanySettings';
import HMRCSettings from './HMRCSettings';
import VATSubmission from './VATSubmission';
import VatSubmissionHistory from './VatSubmissionHistory';
import EmployeeList from './EmployeeList';
import SoloOnlineBookings from './solo/OnlineBookings';

// Supplier Bills & Payments Components
import AgedPayablesReport from './AgedPayablesReport';
import BankImportInterface from './BankImportInterface';
import UnmatchedTransactions from './UnmatchedTransactions';
import SupplierSpendingChart from './SupplierSpendingChart';
import SupplierDirectory from './SupplierDirectory';

// Calculator Components
import CalculatorDashboard from './calculator/CalculatorDashboard';
import BusinessSettings from './calculator/BusinessSettings';
import ServicesManagement from './calculator/ServicesManagement';
import CalculatorServiceEditor from './calculator/ServiceEditor';
import OverheadsCalculator from './calculator/OverheadsCalculator';
import CategoryManagement from './calculator/CategoryManagement';
import ImportPage from './calculator/ImportPage';
import TierManagement from './calculator/TierManagement';

// Solo Dashboard Components
import RecordSale from './solo/RecordSale';
import AddExpense from './solo/AddExpense';
import LogMileage from './solo/LogMileage';
import SoloSettings from './solo/SoloSettings';
import SelfAssessmentForm from './solo/SelfAssessmentForm';
import SoloDataManagement from './solo/SoloDataManagement';
import Bookings from './solo/Bookings';
import Packages from './solo/Packages';
import PackageEditor from './solo/PackageEditor';
import SoloLayout from './solo/SoloLayout';
import Services from './solo/Services';
import Clients from './solo/Clients';
import ShoppingList from './solo/ShoppingList';
import BookingSettings from './solo/BookingSettings';

// Public booking page (no auth)
import PublicBookingRouter from './booking/PublicBookingRouter';


// Pro booking 
import ProDiary from './ProDiary';
import ProBookingSettings from './Probookingsettings';
import ProRecordSale from './ProRecordSale';
import ProTillReconciliation from './ProTillReconciliation';
import ProClientCard from './ProClientCard';
import ProServices from './ProServices';
import ProBookingConfig from './ProBookingConfig';
import ProBookingLanding from './ProBookingLanding';
import ProPackages from './ProPackages';
import ProPackageEditor from './ProPackageEditor';
import ProServicesHub from './ProServicesHub';

// Hub
import HubDashboard from './HubDashboard';

// Document System Components
import DocumentsDashboard from './documents/DocumentsDashboard';
import TemplateLibrary from './documents/TemplateLibrary';
import TemplateCustomization from './documents/TemplateCustomization';
import DocumentSettings from './DocumentSettings';
import ManagerTrainingDashboard from './documents/ManagerTrainingDashboard';
import EmployeeTrainingInterface from './documents/EmployeeTrainingInterface';

// Dashboard Components
import StockDashboard from './dashboards/StockDashboard';
import MoneyDashboard from './dashboards/MoneyDashboard';
import TeamDashboard from './dashboards/TeamDashboard';
import TimeDashboard from './dashboards/TimeDashboard';
import DataManagement from './dashboards/DataManagement';
import EfficiencyAnalyzer from './efficiency/Index';

// Maternity Tracker Components
import MaternityTracker from './maternity/MaternityTracker';
import MaternityForm from './maternity/MaternityForm';
import MaternityDetail from './maternity/MaternityDetail';

import {
  IncomeImport,
  ExpenditureImport,
  SupplierImport,
  EmployeeImport,
  ProductImport,
  ServiceImport,
  TestImport,
  ClientImport,
  ComingSoon
} from './imports';

import {
  IncomeExport,
  ExpenditureExport,
  ProductExport,
  ServiceExport,
  SupplierExport
} from './imports';

import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const theme = createTheme({
  palette: {
    primary: { main: '#2f3542' },
    secondary: { main: '#57606f' },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
      <ErrorBoundary>
        <AuthProvider>
          <PermissionsProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Routes>
                {/* ============================================ */}
                {/* PUBLIC ROUTES                                */}
                {/* ============================================ */}
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<Navigate to="/" replace />} />
                <Route path="/register" element={<Register />} />
                <Route path="/password-recovery" element={<PasswordRecovery />} />
                <Route path="/password-reset/:token" element={<PasswordReset />} />
                <Route path="/registration-success" element={<RegistrationSuccess />} />
                <Route path="/unauthorized" element={<Unauthorized />} />
                <Route path="/apply/:applicationUrlId" element={<EmploymentApplicationForm />} />

                {/* Public Knowledge Base */}
                <Route path="/kb" element={<KnowledgeBaseHome />} />
                <Route path="/kb/categories/:slug" element={<KBCategoryView />} />
                <Route path="/kb/articles/:slug" element={<KBArticle />} />
                
                {/* Public booking page — clients booking appointments */}
                <Route path="/book/:slug" element={<PublicBookingRouter />} />

                {/* ============================================ */}
                {/* PROTECTED ROUTES - NO LAYOUT                 */}
                {/* ============================================ */}

                {/* Rental Routes */}
                <Route
                  element={
                    <ProtectedRoute allowedRoles={['RENTAL_USER', 'ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                      <RentalLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/rental" element={<RentalDashboard />} />
                </Route>

                {/* Hub Dashboard - Full Screen, No Sidebar */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['ADMIN', 'ACCOUNT_OWNER']}>
                      <HubDashboard />
                    </ProtectedRoute>
                  }
                />
                
                {/* ===== PRO DIARY ===== */}
                  <Route
                    path="/pro/diary"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                        <ProDiary />
                      </ProtectedRoute>
                    }
                  />
                  {/* ===== PRO RECORD SALE ===== */}
                  <Route
                    path="/pro/record-sale/:bookingId/:source"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                        <ProRecordSale />
                      </ProtectedRoute>
                    }
                  />
                  {/* ===== PRO TILL RECONCILIATION ===== */}
                  <Route
                    path="/pro/till-reconciliation"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                        <ProTillReconciliation />
                      </ProtectedRoute>
                    }
                  />
                  {/* ===== PRO CLIENT CARD ===== */}
                  <Route
                    path="/pro/clients/:clientId"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                        <ProClientCard />
                      </ProtectedRoute>
                    }
                  />
                  {/* ===== PRO SERVICES ===== */}
                  <Route
                    path="/pro/services"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                        <ProServices />
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* ===== PRO PACKAGES ===== */}

                  <Route
                    path="/pro/packages"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                        <ProPackages />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/pro/packages/:id"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                        <ProPackageEditor />
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* ===== PRO SERVICES HUB ===== */}

                  <Route
                    path="/pro/services-hub"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                        <ProServicesHub />
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Online Booking landing */}
                  <Route
                    path="/pro/booking-settings"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                        <ProBookingLanding />
                      </ProtectedRoute>
                    }
                  />

                <Route
                  path="/employeeportal"
                  element={<EmployeePortal />}
                />
                {/* Employee Portal - Time Off Request (No Sidebar) */}
                <Route
                  path="/employeeportal/time-off-request"
                  element={<TimeOffRequestForm />}
                />

                {/* Legacy redirect */}
                <Route path="/home-old" element={<Navigate to="/dashboard" replace />} />

                {/* Section Dashboards - Full Screen, No Sidebar */}
                <Route
                  path="/stock"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="stock">
                      <StockDashboard />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/money"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="money">
                      <MoneyDashboard />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/team"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="team">
                      <TeamDashboard />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/time"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']} requiredPermission="time">
                      <TimeDashboard />
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/import"
                  element={
                    <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'SOLO']} requiredPermission="import_data">
                      <DataManagement />
                    </ProtectedRoute>
                  }
                />

                {/* ============================================ */}
                {/* PROTECTED ROUTES - WITH LAYOUT & SIDEBAR    */}
                {/* ============================================ */}
                <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>

                  {/* Operations Dashboard */}
                  <Route
                    path="/home"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                        <MainDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/forecast-dashboard"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                        <WeeklyForecastDashboard />
                      </ProtectedRoute>
                    }
                  />

                  {/* Backward compatibility routes */}
                  <Route path="/financial" element={<Navigate to="/money" replace />} />
                  <Route path="/owner_dashboard" element={<Navigate to="/home" replace />} />

                  {/* ===== CALCULATOR SECTION ===== */}
                  <Route
                    path="/calculator"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="calculator">
                        <CalculatorDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/calculator/settings"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="calculator">
                        <BusinessSettings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/calculator/services"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="calculator">
                        <ServicesManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/calculator/services/new"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="calculator">
                        <CalculatorServiceEditor />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/calculator/services/:serviceId"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="calculator">
                        <CalculatorServiceEditor />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/calculator/overheads"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="calculator">
                        <OverheadsCalculator />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/calculator/categories"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="calculator">
                        <CategoryManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/calculator/import"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="calculator">
                        <ImportPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/calculator/tiers"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="calculator">
                        <TierManagement />
                      </ProtectedRoute>
                    }
                  />

                  {/* ===== STOCK SECTION ===== */}
                  <Route
                    path="/stock/products"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="stock">
                        <ProductManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/stock/transactions"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="stock">
                        <StockTransactions />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/stock/service-products/:serviceId"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="stock">
                        <ServiceProductManager />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/stock/purchase-orders"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="stock">
                        <PurchaseOrders />
                      </ProtectedRoute>
                    }
                  />

                  {/* ===== MONEY SECTION ===== */}
                  <Route
                    path="/income_card"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="money">
                        <IncomeCard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/income"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="money">
                        <IncomeCard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/income/entry"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="money">
                        <DailyIncomeEntry />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/supplier_card"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="money">
                        <SupplierCard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/supplier_directory"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="money">
                        <SupplierDirectory />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/efficiency-analyzer"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="money">
                        <EfficiencyAnalyzer />
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* ===== PRO ONLINE BOOKING ===== */}
                  {/* Stylist & service matrix — was the old /pro/booking-settings */}
                  <Route
                    path="/pro/booking-settings/stylists"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                        <ProBookingSettings />
                      </ProtectedRoute>
                    }
                  />

                  {/* Booking configuration — hours, deposit, Stripe, slug */}
                  <Route
                    path="/pro/booking-config"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                        <ProBookingConfig />
                      </ProtectedRoute>
                    }
                  />

                  {/* ===== SUPPLIER BILLS & PAYMENTS ===== */}
                  <Route
                    path="/money/bank-import"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="money">
                        <BankImportInterface />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/money/unmatched-transactions"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="money">
                        <UnmatchedTransactions />
                      </ProtectedRoute>
                    }
                  />

                  {/* ===== TEAM SECTION ===== */}
                  <Route
                    path="/employees"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="team">
                        <EmployeeList />  {/* NEW - List view */}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/employee_card"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="team">
                        <EmployeeCard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/employee_reviews"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="team">
                        <EmployeeReviews />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/submit_employee_review"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="team">
                        <SubmitEmployeeReview />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/submit_training"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="manage_training">
                        <SubmitTraining />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/disciplinary"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="team">
                        <Disciplinary />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/employment_applications"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="team">
                        <ManageApplications />
                      </ProtectedRoute>
                    }
                  />

                  {/* ===== MATERNITY TRACKER ===== */}
                  <Route
                    path="/team/maternity"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="team">
                        <MaternityTracker />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team/maternity/new"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="team">
                        <MaternityForm />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/team/maternity/:id"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="team">
                        <MaternityDetail />
                      </ProtectedRoute>
                    }
                  />

                  {/* ===== TIME SECTION ===== */}
                  {/* NEW: Unified Time Off Request Form */}
                  <Route
                    path="/time-off/request"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']} requiredPermission="time">
                        <TimeOffRequestForm />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/time-management"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']} requiredPermission="time">
                        <HolidayCalendar />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/holiday_calendar"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']} requiredPermission="time">
                        <HolidayCalendar />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/submit_absence"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']} requiredPermission="time">
                        <SubmitAbsence />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/time-off/approval"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="approve_holidays">
                        <TimeOffApproval />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/time-off/my-requests"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']}>
                        <MyTimeOffRequests />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/time-off-hub"
                    element={
                      <ProtectedRoute allowedRoles={["ACCOUNT_OWNER", "ACCOUNT_MANAGER", "ACCOUNT_USER"]}>
                        <TimeOffHub />
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/bradford-factor"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="team">
                        <BradfordFactorDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/leave-balances"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="time">
                        <LeaveBalances />
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Timesheet - Manager view */}
                  <Route
                    path="/timesheet"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="time">
                        <Timesheet />
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route 
                    path="/payroll-calculator" 
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']} requiredPermission="time">
                        <PayrollCalculator />
                      </ProtectedRoute>
                    } 
                  />
                  
                  <Route
                    path="/payroll-export"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="time">
                        <PayrollExport />
                      </ProtectedRoute>
                    }
                  />

                  {/* Clock In/Out - Standalone page for all staff */}
                  <Route
                    path="/clock"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']} requiredPermission="time">
                        <ClockWidget />
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Employee: Submit and view own requests */}
                  <Route
                    path="/flexible-working/request"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']} requiredPermission="time">
                        <FlexibleWorkingRequestForm />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/flexible-working/my-requests"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']} requiredPermission="time">
                        <MyFlexibleWorkingRequests />
                      </ProtectedRoute>
                    }
                  />

                  {/* Manager: Approval queue */}
                  <Route
                    path="/flexible-working/approval"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="approve_holidays">
                        <FlexibleWorkingApproval />
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Rota Dashboard - Managers/Owners only */}
                  <Route
                    path="/rota"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                        <RotaDashboard />
                      </ProtectedRoute>
                    }
                  />

                  {/* Rota Builder - Managers/Owners only */}
                  <Route
                    path="/rota/builder"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                        <RotaBuilder />
                      </ProtectedRoute>
                    }
                  />

                  {/* My Schedule - ALL users (employees see their own shifts) */}
                  <Route
                    path="/rota/my-schedule"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']}>
                        <MySchedule />
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route 
                    path="/ssp-calculator" 
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                        <SSPCalculator />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* ===== REPORTS SECTION ===== */}
                  <Route
                    path="/reports"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="reports">
                        <ReportsDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="reports">
                        <ReportsDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/tax"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="reports">
                        <TaxReport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/cashflow"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="reports">
                        <CashflowReport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/profit-loss"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="reports">
                        <ProfitLossReport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/transaction"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="reports">
                        <TransactionReport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/vat"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="reports">
                        <VatDashboard />
                      </ProtectedRoute>
                    }
                  />

                  {/* ===== SUPPLIER BILLS REPORTS ===== */}
                  <Route
                    path="/reports/aged-payables"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="reports">
                        <AgedPayablesReport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reports/supplier-spending"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="reports">
                        <SupplierSpendingChart />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/vat_dashboard"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="reports">
                        <VatDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/audit-trail"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                        <AuditTrail />
                      </ProtectedRoute>
                    }
                  />

                  {/* ===== COMMISSION ===== */}
                  <Route
                    path="/commission/dashboard"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="commission">
                        <CommissionDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/commission/types/new"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="commission">
                        <CommissionTypeEditor />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/commission/types/:id"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="commission">
                        <CommissionTypeEditor />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/commission/employee/:employeeId"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="commission">
                        <EmployeeCommission />
                      </ProtectedRoute>
                    }
                  />

                  {/* ===== DATA IMPORT ===== */}
                  <Route
                    path="/import/test"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']} requiredPermission="import_data">
                        <TestImport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/import/income"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'SOLO']} requiredPermission="import_data">
                        <IncomeImport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/import/expenditure"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'SOLO']} requiredPermission="import_data">
                        <ExpenditureImport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/import/suppliers"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'SOLO']} requiredPermission="import_data">
                        <SupplierImport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/import/employees"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'SOLO']} requiredPermission="import_data">
                        <EmployeeImport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/import/products"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'SOLO']} requiredPermission="import_data">
                        <ProductImport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/import/services"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'SOLO']} requiredPermission="import_data">
                        <ServiceImport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/import/clients"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']} requiredPermission="import_data">
                        <ClientImport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/export/income"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'SOLO']} requiredPermission="import_data">
                        <IncomeExport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/export/expenditure"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'SOLO']} requiredPermission="import_data">
                        <ExpenditureExport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/export/products"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'SOLO']} requiredPermission="import_data">
                        <ProductExport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/export/services"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'SOLO']} requiredPermission="import_data">
                        <ServiceExport />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/export/suppliers"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'SOLO']} requiredPermission="import_data">
                        <SupplierExport />
                      </ProtectedRoute>
                    }
                  />
                  {/* ===== COMMON FEATURES ===== */}
                  <Route
                    path="/notes"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']}>
                        <Notes />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/reminders"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']}>
                        <Reminders />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']}>
                        <PersonalInfo />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/personal_info"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']}>
                        <PersonalInfo />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/messages"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']}>
                        <MessagesDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/document_approval"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="documents">
                        <DocumentApproval />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/rental_dashboard"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                        <RentalDashboard />
                      </ProtectedRoute>
                    }
                  />

                  {/* ===== DOCUMENTS SECTION ===== */}
                  <Route
                    path="/documents"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="documents">
                        <DocumentsDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/documents/templates"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="documents">
                        <TemplateLibrary />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/documents/customize/:templateId"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="documents">
                        <TemplateCustomization />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/documents/settings"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="documents">
                        <DocumentSettings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/documents/training"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']} requiredPermission="manage_training">
                        <ManagerTrainingDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/documents/training/employee"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER', 'ACCOUNT_USER']}>
                        <EmployeeTrainingInterface />
                      </ProtectedRoute>
                    }
                  />

                  {/* ===== SETTINGS - OWNER ONLY ===== */}
                  <Route
                    path="/settings/manager-permissions"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                        <ManagerPermissions />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/settings/company" 
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                        <CompanySettings />
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* ===== HMRC MTD SECTION ===== */}
                  <Route
                    path="/settings/hmrc"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                        <HMRCSettings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/vat/submit/:obligationId"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                        <VATSubmission />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/vat/history"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER']}>
                        <VatSubmissionHistory />
                      </ProtectedRoute>
                    }
                  />

                  {/* ===== ADMIN & MANAGER DASHBOARDS ===== */}
                  <Route
                    path="/admin_dashboard"
                    element={
                      <ProtectedRoute allowedRoles={['ADMIN']}>
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/manager_dashboard"
                    element={
                      <ProtectedRoute allowedRoles={['ACCOUNT_OWNER', 'ACCOUNT_MANAGER']}>
                        <ManagerDashboard />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                {/* ===== SOLO DASHBOARD - WITH SOLOLAYOUT WRAPPER ===== */}
                <Route
                  element={
                    <ProtectedRoute allowedRoles={['SOLO', 'ACCOUNT_OWNER']}>
                      <SoloLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/solo-dashboard" element={<SoloDashboard />} />
                  <Route path="/solo/bookings" element={<Bookings />} />
                  <Route path="/solo/services" element={<Services />} />
                  <Route path="/solo/packages" element={<Packages />} />
                  <Route path="/solo/packages/new" element={<PackageEditor />} />
                  <Route path="/solo/packages/:id" element={<PackageEditor />} />
                  <Route path="/solo/clients" element={<Clients />} />
                  <Route path="/solo/record-sale" element={<RecordSale />} />
                  <Route path="/solo/record-sale/:bookingId" element={<RecordSale />} />
                  <Route path="/solo/add-expense" element={<AddExpense />} />
                  <Route path="/solo/log-mileage" element={<LogMileage />} />
                  <Route path="/solo/settings" element={<SoloSettings />} />
                  <Route path="/solo/booking-settings" element={<BookingSettings />} />
                  <Route path="/solo/self-assessment" element={<SelfAssessmentForm />} />
                  <Route path="/solo/import-data" element={<SoloDataManagement />} />
                  <Route path="/solo/shopping-list" element={<ShoppingList />} />
                  <Route path="/solo/online-bookings" element={<SoloOnlineBookings />} />

                  {/* Calculator available to SOLO users */}
                  <Route path="/solo/calculator" element={<CalculatorDashboard />} />
                  <Route path="/solo/calculator/settings" element={<BusinessSettings />} />
                  <Route path="/solo/calculator/services" element={<ServicesManagement />} />
                  <Route path="/solo/calculator/services/new" element={<CalculatorServiceEditor />} />
                  <Route path="/solo/calculator/services/:serviceId" element={<CalculatorServiceEditor />} />
                  <Route path="/solo/calculator/overheads" element={<OverheadsCalculator />} />
                  <Route path="/solo/calculator/categories" element={<CategoryManagement />} />
                  <Route path="/solo/calculator/import" element={<ImportPage />} />
                </Route>

                {/* Catch-all: redirect to login */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </LocalizationProvider>
          </PermissionsProvider>
        </AuthProvider>
      </ErrorBoundary>
    </Router>
    </ThemeProvider>
  );
}

export default App;